import { TooltipWrapper } from "@app/shared/index";
import { NewActionMenu } from "@app/shared/action-menu";
import { QESRadioButton, QESRadioGroup } from "@app/shared/qes-standards";
import { FC, useContext, useEffect, useState } from "react";
import { FeatureFlagContext, Features } from "@app/feature-flags";

interface DefaultViewFilterProps {
  // The suffix to append to the local storage key for this filter. Adequacy projects should use '' for backwards
  // compatibility.
  filterSuffix: string;
  // The list of filter options to display in the dropdown.
  filterOptions: string[];
  // The callback that DefaultViewFilter will call when the filter is changed. The new filter value (from the list of
  // passed in filterOptions) is passed as an argument, along with an indicator of whether to force a refresh of
  // the filter
  onChange: (filter: string, forceRefresh: boolean) => void;
}

/* Dropdown menu to select and store a string filter value in local storage. The key will be viewbyfilter, followed
* by any filterSuffix. */
const DefaultViewFilter: FC<DefaultViewFilterProps> = ({filterSuffix, filterOptions, onChange}) => {
  const [selectedFilter, setSelectedFilter] = useState<string>(filterOptions[0]);
  const featureFlagContext = useContext(FeatureFlagContext);

  useEffect(() => {
    if (!featureFlagContext.enabledFeatureFlags.has(Features.ENABLE_DEFAULT_VIEW_BY)) {
      return;
    }
    const filter = localStorage.getItem(`viewbyfilter${filterSuffix}`);
    if (filter) {
      setSelectedFilter(filter);
      onChange(filter, false);
    }
  }, []);

  const handleFilterChange = (filter: string) => {
    localStorage.setItem(`viewbyfilter${filterSuffix}`, filter);
    setSelectedFilter(filter);
    onChange(filter, true);
  }

  if (!featureFlagContext.enabledFeatureFlags.has(Features.ENABLE_DEFAULT_VIEW_BY)) {
    return null;
  }

  return (
    <TooltipWrapper text="Selecting a Default View below will apply to all projects">
      <NewActionMenu
        buttonText={<><i className="fa fa-filter"/> Default View </>}
        alignRight={false}
        testId="default-view-filter__default-filter"
        buttonClassNames="qes-button qes-button-secondary h-100"
      >
        <QESRadioGroup groupContainerClass="col-12 mt0">
          {filterOptions.map((filter, index) => (
            <QESRadioButton
              key={index}
              label={filter}
              checked={filter === selectedFilter}
              onChange={() => handleFilterChange(filter)}
              testId={`default-view-filter__view-by-${index}`}
            />
          ))}
        </QESRadioGroup>
      </NewActionMenu>
    </TooltipWrapper>

  );
}

export default DefaultViewFilter;